import { ReactElement, ReactNode, useMemo } from 'react';
import { useMessageSearchAnalytics } from 'src/hooks/analytics/chat/useMessageSearchAnalytics.hook';
import { normalizeSearchTerm } from 'src/pages/MessengerV2/MessageSearch.functions';
import useMessageSearchEnabled from 'src/pages/MessengerV2/MessageSearchEnabled.hook';
import { ChatSearchContext } from './ChatSearch.context';
import useChatSearch from './ChatSearch.hook';
import { tChatSearchContext } from './ChatSearch.types';

type tChatSearchProviderProps = { children: ReactNode };

function EnabledChatSearchProvider(
  props: tChatSearchProviderProps,
): ReactElement {
  const { children } = props;
  const chatSearch = useChatSearch();

  // ANA-1. Here, in the one instance per chat panel, and not in the bar or in
  // the magnifier: a hook that emits runs once per consumer. A bar that goes
  // away without "Cerrar" (another contact opened) is a search that was left.
  const { settle, trackOpened } = useMessageSearchAnalytics({
    surface: 'conversation',
    isSessionOpen: chatSearch.isOpen,
    endedBy: 'left',
    typedTerm: normalizeSearchTerm(chatSearch.inputValue),
    answered: chatSearch.answered,
    resultCount: chatSearch.matches.length,
    hasMore: chatSearch.hasMore,
    isLoadingMore: chatSearch.loadingMore,
  });

  // The gestures reach the search untouched, and then say what they were. The
  // search goes first: what it does never waits on analytics. With no match
  // yet, Enter is "Seguir buscando" from the keyboard: the query has not been
  // answered in full, so it does not settle.
  const value = useMemo<tChatSearchContext>(() => ({
    ...chatSearch,
    open: (options) => {
      chatSearch.open(options);
      trackOpened(options?.trigger ?? 'shortcut');
    },
    close: (options) => {
      chatSearch.close(options);
      settle('closed');
    },
    older: () => {
      chatSearch.older();
      if (chatSearch.matches.length) settle('stepped');
    },
    newer: () => {
      chatSearch.newer();
      if (chatSearch.matches.length) settle('stepped');
    },
  }), [chatSearch, trackOpened, settle]);

  return (
    <ChatSearchContext.Provider value={value}>
      {children}
    </ChatSearchContext.Provider>
  );
}

/**
 * The in-chat search for ONE chat panel. Mounted inside each ChatProvider, so
 * the Messenger page and the Pipe and Directory chat modals each own theirs.
 *
 * Behind useMessageSearchEnabled (the VITE_MESSAGE_SEARCH_FEATURE kill switch
 * AND the workspace's stored `chat_message-search` flag): off, it renders its
 * children bare, the context stays null and every consumer (magnifier,
 * Ctrl/⌘+F, bar, marks) renders nothing — the chat is exactly what it was.
 */
export default function ChatSearchProvider(
  props: tChatSearchProviderProps,
): ReactElement {
  const { children } = props;
  const isEnabled = useMessageSearchEnabled();

  if (!isEnabled) return <>{children}</>;

  return <EnabledChatSearchProvider>{children}</EnabledChatSearchProvider>;
}
